import { useStartImpersonation } from '@pa/api/admin'
import { toastApiError } from '@pa/staff'
import { Modal, useToast } from '@pa/ui'
import { UserCog } from 'lucide-react'
import { useId, useState } from 'react'
import { useExternalNav } from '../context'
import { actAsCopy, clinicLandingHref, rememberStartedImpersonation } from '../model/state'

export interface ActAsClinicAdminProps {
  clinic: { id: string; name: string }
  /** S-02 rows use the small button. */
  size?: 'sm'
}

/**
 * S-06 Act as clinic admin (UserFlows S-06; Frontend §7.3): the button on the S-04 header and S-02 rows, its confirm
 * modal (Continue / Cancel), then #57 and the clinic app's `/impersonate#imp={id}` in the same tab. Allowed for an
 * inactive clinic (the banner adds "(inactive clinic)").
 */
export function ActAsClinicAdmin({ clinic, size }: ActAsClinicAdminProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className={`btn btn-secondary ${size === 'sm' ? 'btn-sm' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <UserCog aria-hidden /> Act as clinic admin
      </button>
      {open && (
        <ActAsDialog
          clinic={clinic}
          onClose={() => {
            setOpen(false)
          }}
        />
      )}
    </>
  )
}

function ActAsDialog({ clinic, onClose }: { clinic: { id: string; name: string }; onClose: () => void }) {
  const id = useId()
  const toast = useToast()
  const { assign, clinicOrigin } = useExternalNav()
  const start = useStartImpersonation()
  const copy = actAsCopy(clinic.name)

  async function proceed() {
    try {
      const session = await start.mutateAsync({ clinicId: clinic.id, data: {} })
      rememberStartedImpersonation(session.id)
      assign(clinicLandingHref(clinicOrigin, session.id))
    } catch (err) {
      toastApiError(toast, err)
      onClose()
    }
  }

  return (
    <Modal onClose={onClose} labelledBy={`${id}-title`} describedBy={`${id}-body`}>
      <div className="modal-head">
        <div>
          <h2 id={`${id}-title`}>{copy.title}</h2>
        </div>
      </div>
      <div className="modal-body">
        <p id={`${id}-body`}>{copy.body}</p>
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" disabled={start.isPending} onClick={() => void proceed()}>
          Continue
        </button>
      </div>
    </Modal>
  )
}
